import "context-filter-polyfill";
import { Suspense, useEffect } from "react";
import { Switch, useHistory, useLocation } from "react-router-dom";

import { ChunkLoadErrorBoundary } from "./components/ChunkLoadErrorBoundary";
import { SuspenceFallbackComponent } from "./components/SuspenceFallback";
import { NavigationComponent } from "./components/Navigation";
import { SupportFeedbackModal } from "./components/SupportFeedbackModal";
import TeamPopupLayer from "./components/TeamPopupLayer";
import { CustomRoute } from "./components/CustomRoute";
import { Routes } from "./utils/routes";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import "./utils/browser";
import { AppServiceInstance } from "./services/app";
import "./services/moment";
import { StorageServiceInstance } from "./services/storage";
import "./services/game";
import { cognitoGetSession } from "./services/cognito-auth";
import { getMeRequest } from "./axios/routes/user";
import { setBearerToken } from "./axios/instance";
import { trackFanPageView } from "./utils/clientAnalytics";
import { bootstrapAnalyticsConsent, setAnalyticsProfileIdReader } from "./utils/analyticsConsent";
import { bootstrapFlowPreview } from "./utils/flowPreviewBootstrap";
import { pendingMomentoSignOutStamp, rememberMomentoSignOutStamp } from "./utils/clearMomentoSiteData";

import "./styles/main.scss";

setAnalyticsProfileIdReader(
  () => String(StorageServiceInstance.selectedCustomerId || ""),
);

const FAN_SESSION_RESTORE_PATHS = new Set<string>([
  Routes.SEAT,
  Routes.DETAILS,
  Routes.PLACE,
  Routes.SIGNUP,
  Routes.ENHANCED_GA_WHERE,
  Routes.ENHANCED_GA_AREA,
  Routes.ENHANCED_GA_MAP,
  Routes.MY_MOMENTS,
]);

const WelcomePage = lazyWithRetry(() => import("./pages/welcome"));
const TeamsPickerPage = lazyWithRetry(() => import("./pages/teams"));
const GamesPage = lazyWithRetry(() => import("./pages/games"));
const SeatPage = lazyWithRetry(() => import("./pages/seat"));
const EnhancedGaWherePage = lazyWithRetry(() => import("./pages/enhancedGaWhere"));
const EnhancedGaAreaPage = lazyWithRetry(() => import("./pages/enhancedGaArea"));
const EnhancedGaMapPage = lazyWithRetry(() => import("./pages/enhancedGaMap"));
const FindPlacePage = lazyWithRetry(() => import("./pages/place"));
const DetailsPage = lazyWithRetry(() => import("./pages/userDetails"));
const MyMomentsPage = lazyWithRetry(() => import("./pages/myMoments"));
const MomentPage = lazyWithRetry(() => import("./pages/moment"));
const MomentoPage = lazyWithRetry(() => import("./pages/momento"));
const AllMomentoPage = lazyWithRetry(() => import("./pages/allMomento"));
const EditorPage = lazyWithRetry(() => import("./pages/editor"));
const DownloadOptionsPage = lazyWithRetry(() => import("./pages/downloadOptions"));
const ViewImagePage = lazyWithRetry(() => import("./pages/viewImage"));
const signupPage = lazyWithRetry(() => import("./pages/signup"));
const moreinfoPage = lazyWithRetry(() => import("./pages/moreinfo"));
const momentoOptionsPage = lazyWithRetry(() => import("./pages/momentoOptions"));
const ProfilePage = lazyWithRetry(() => import("./pages/profile"));
const ProfileGalleryPage = lazyWithRetry(() => import("./pages/profile/gallery"));
const verifySeatPage = lazyWithRetry(() => import("./pages/verifySeat"));
const OptOutPage = lazyWithRetry(() => import("./pages/optOut"));
const CookieSettingsPage = lazyWithRetry(() => import("./pages/cookieSettings"));
const CookiePolicyPage = lazyWithRetry(() => import("./pages/teamPolicy"));
const TermsOfUsePage = lazyWithRetry(() => import("./pages/teamPolicy/terms"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/teamPolicy/privacy"));

export const App = (): JSX.Element => {
  const { pathname } = useLocation();
  const history = useHistory();

  useEffect(() => {
    // Close navigation bar on route change
    AppServiceInstance.closeNavigationBar();
  }, [pathname]);

  useEffect(() => {
    // Set the custom history object
    AppServiceInstance.setHistory(history);
  }, [history]);

  useEffect(() => {
    const boot = async () => {
      const signOutStamp = pendingMomentoSignOutStamp();
      if (signOutStamp) {
        await StorageServiceInstance.resetAllStateForSignOut();
        rememberMomentoSignOutStamp(signOutStamp);
      }
      StorageServiceInstance.hydrateFromWorkerStorage();
      bootstrapFlowPreview().catch(() => undefined);
      bootstrapAnalyticsConsent().catch(() => undefined);
    };
    boot().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!FAN_SESSION_RESTORE_PATHS.has(pathname)) return;
    StorageServiceInstance.restoreFanSessionFromWorker({
      loadSeatSections: pathname === Routes.SEAT,
    }).catch(() => undefined);
  }, [pathname]);

  useEffect(() => {
    const restoreCognitoProfile = async () => {
      try {
        const tokens = await cognitoGetSession();
        setBearerToken(tokens.idToken);
        const { profile } = await getMeRequest(tokens.idToken);
        if (profile) {
          StorageServiceInstance.restoreReturningFanSession(profile);
        }
      } catch {
        // No Cognito session or failed to load profile - ignore
      }
    };
    restoreCognitoProfile();
  }, []);

  useEffect(() => {
    trackFanPageView(pathname, typeof document !== "undefined" ? document.title : "")
      .catch(() => undefined);
  }, [pathname]);

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<SuspenceFallbackComponent />}>
        <NavigationComponent />
        <SupportFeedbackModal />
        <TeamPopupLayer />
        <Switch>
          <CustomRoute exact path={Routes.WELCOME} Component={WelcomePage} />
          <CustomRoute exact path={Routes.TEAMS} Component={TeamsPickerPage} />
          <CustomRoute exact path={Routes.GAMES} Component={GamesPage} />
          <CustomRoute exact path={Routes.ENHANCED_GA_WHERE} Component={EnhancedGaWherePage} />
          <CustomRoute exact path={Routes.ENHANCED_GA_AREA} Component={EnhancedGaAreaPage} />
          <CustomRoute exact path={Routes.ENHANCED_GA_MAP} Component={EnhancedGaMapPage} />
          <CustomRoute exact path={Routes.SEAT} Component={SeatPage} />
          <CustomRoute exact path={Routes.PLACE} Component={FindPlacePage} />
          <CustomRoute exact path={Routes.DETAILS} Component={DetailsPage} />
          <CustomRoute exact path={Routes.MY_MOMENTS} Component={MyMomentsPage} />
          <CustomRoute exact path={Routes.MOMENT} Component={MomentPage} />
          <CustomRoute exact path={Routes.MOMENTO} Component={MomentoPage} />
          <CustomRoute exact path={Routes.ALL_MOMENTO} Component={AllMomentoPage} />
          <CustomRoute exact path={Routes.EDITOR} Component={EditorPage} />
          <CustomRoute exact path={Routes.DOWNLOAD_OPTIONS} Component={DownloadOptionsPage} />
          <CustomRoute exact path={Routes.IMAGE} Component={ViewImagePage} />
          <CustomRoute exact path={Routes.SIGNUP} Component={signupPage} />
          <CustomRoute exact path={Routes.MOREINFO} Component={moreinfoPage} />
          <CustomRoute exact path={Routes.MOMENTO_OPTIONS} Component={momentoOptionsPage} />
          <CustomRoute exact path={Routes.PROFILE} Component={ProfilePage} />
          <CustomRoute exact path={Routes.PROFILE_GALLERY} Component={ProfileGalleryPage} />
          <CustomRoute exact path={Routes.VERIFY_SEAT} Component={verifySeatPage} />
          <CustomRoute exact path={Routes.OPT_OUT} Component={OptOutPage} />
          <CustomRoute exact path={Routes.COOKIE_SETTINGS} Component={CookieSettingsPage} />
          <CustomRoute exact path={Routes.COOKIE_POLICY} Component={CookiePolicyPage} />
          <CustomRoute exact path={Routes.TERMS_OF_USE} Component={TermsOfUsePage} />
          <CustomRoute exact path={Routes.PRIVACY_POLICY} Component={PrivacyPolicyPage} />
        </Switch>
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
};
